import type {
  EntityInvestmentBoardDto,
  EntityInvestmentBoardEntryDto,
  MyInvestmentBoardDto,
  MyInvestmentBoardEntryDto,
} from '@/api/investing-board/types';
import {
  type PipelineBoard,
  type PipelineCard,
  type PipelineColumn,
} from '@/pages/pipelines/common/types';
import { getPipelinePersonLabel, isPresent } from '@/pages/pipelines/common/utils/mapping';
import { formatMoney } from '@/utils/numbers/formatters';

import { getInvestingBoardStageLabel } from './board-stages';

const INVESTING_BOARD_PIPELINE = 'investing-board';
const UNNAMED_FUND = 'Unnamed fund';

type PledgedDto = { amount: string; denomination: string } | null;

type BoardColumnShape<TEntry> = {
  stage: string;
  order: number;
  final: boolean;
  entries: TEntry[];
};

const getFundLabel = (fund: { name: string | null }): string => fund.name?.trim() || UNNAMED_FUND;

const getPledgedLine = (pledged: PledgedDto): string | null =>
  pledged ? `Pledged ${formatMoney(Number(pledged.amount), pledged.denomination)}` : null;

const computeTotals = (columns: PipelineColumn[]): PipelineBoard['totals'] => {
  const cards = columns.reduce((sum, column) => sum + column.cards.length, 0);
  const activeCards = columns
    .filter((column) => !column.terminal)
    .reduce((sum, column) => sum + column.cards.length, 0);

  return { cards, activeCards, nonActiveCards: cards - activeCards };
};

const buildColumns = <TEntry>(
  columns: BoardColumnShape<TEntry>[],
  toCard: (_entry: TEntry, _columnFinal: boolean) => PipelineCard,
): PipelineColumn[] =>
  columns.map((column) => {
    const cards = column.entries.map((entry) => toCard(entry, column.final));

    return {
      stage: column.stage,
      label: getInvestingBoardStageLabel(column.stage),
      order: column.order,
      terminal: column.final,
      count: cards.length,
      cards,
    };
  });

const mapMyEntryToCard = (
  entry: MyInvestmentBoardEntryDto,
  columnFinal: boolean,
): PipelineCard => ({
  id: entry.id,
  pipeline: INVESTING_BOARD_PIPELINE,
  stage: entry.stage,
  active: !columnFinal,
  title: getFundLabel(entry.fund),
  subtitleLines: [getPledgedLine(entry.pledged)].filter(isPresent),
  badges: [],
  indicators: [],
  timer: null,
  lastStageChangedAt: entry.updatedAt,
  entityRefs: { fundId: entry.fund.id },
});

export const mapMyInvestmentBoardToPipelineBoard = (board: MyInvestmentBoardDto): PipelineBoard => {
  const columns = buildColumns(board.columns, mapMyEntryToCard);

  return {
    pipeline: INVESTING_BOARD_PIPELINE,
    columns,
    filterDefinitions: [],
    totals: computeTotals(columns),
  };
};

const mapEntityEntryToCard = (
  entry: EntityInvestmentBoardEntryDto,
  columnFinal: boolean,
): PipelineCard => ({
  id: entry.id,
  pipeline: INVESTING_BOARD_PIPELINE,
  stage: entry.stage,
  active: !columnFinal,
  title: getFundLabel(entry.fund),
  subtitleLines: [getPipelinePersonLabel(entry.investor), getPledgedLine(entry.pledged)].filter(
    isPresent,
  ),
  badges: [],
  indicators: [
    {
      key: 'followingFund',
      label: 'Following fund',
      icon: 'eye',
      active: entry.followingFund,
    },
  ],
  timer: null,
  lastStageChangedAt: entry.updatedAt,
  entityRefs: { fundId: entry.fund.id, investorUserId: entry.investor.id },
});

export const mapEntityInvestmentBoardToPipelineBoard = (
  board: EntityInvestmentBoardDto,
): PipelineBoard => {
  const columns = buildColumns(board.columns, mapEntityEntryToCard);

  return {
    pipeline: INVESTING_BOARD_PIPELINE,
    columns,
    filterDefinitions: [],
    totals: computeTotals(columns),
  };
};
